/**
 * Pure step logic for the customer helper, kept apart from React so it is easy to test.
 * Rules: one question per screen, no disabled buttons (pressing explains why via aria-live).
 */
import type { Draft, DraftRequest, PublicPage, PublicQuestion, Tone } from "@/lib/api/types";

export type Step = 1 | 2 | 3;
export const STEP_COUNT = 3;

export interface Toggled {
  list: string[];
  notice: string;
}

export function toggleSelection(
  list: readonly string[],
  id: string,
  max: number,
  kind: "good" | "bad"
): Toggled {
  if (list.includes(id)) return { list: list.filter((x) => x !== id), notice: "" };
  if (list.length >= max) {
    const what = kind === "good" ? "좋았던 점" : "아쉬웠던 점";
    return {
      list: [...list],
      notice: `${what}은 ${max}개까지 고를 수 있어요. 하나를 빼고 골라 주세요.`,
    };
  }
  return { list: [...list, id], notice: "" };
}

export function step1Notice(good: readonly string[]): string | null {
  return good.length === 0 ? "좋았던 점을 하나만 골라 주셔도 돼요." : null;
}

export function missingRequired(
  questions: readonly PublicQuestion[],
  answers: Readonly<Record<string, string>>
): PublicQuestion | null {
  return questions.find((q) => q.required && !answers[q.id]) ?? null;
}

export function requiredNotice(q: PublicQuestion): string {
  return `${q.prompt} 하나만 골라 주세요. 이 질문은 꼭 답해 주셔야 초안을 만들 수 있어요.`;
}

/** Answers for questions that no longer exist (or options that vanished) are dropped. */
export function cleanAnswers(
  questions: readonly PublicQuestion[],
  answers: Readonly<Record<string, string>>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    const a = answers[q.id];
    if (a && q.options.some((o) => o.id === a)) out[q.id] = a;
  }
  return out;
}

export function buildDraftRequest(
  page: Pick<PublicPage, "questions" | "platforms">,
  s: { good: string[]; bad: string[]; answers: Record<string, string>; tone: Tone }
): DraftRequest {
  const primary = page.platforms[0];
  return {
    good: [...s.good],
    bad: [...s.bad],
    answers: cleanAnswers(page.questions, s.answers),
    tone: s.tone,
    ...(primary ? { platform_id: primary.id } : {}),
  };
}

/** True when the customer changed the chosen draft before copying (the truthfulness signal). */
export function isEdited(draft: Draft | undefined, text: string, title: string): boolean {
  if (!draft) return text.trim().length > 0;
  if (draft.text.trim() !== text.trim()) return true;
  return (draft.title ?? "").trim() !== title.trim();
}

/** Sponsored pages must keep the disclosure line; put it back if the customer removed it. */
export function ensureTail(text: string, tail: string): { text: string; restored: boolean } {
  // Tolerate a dropped final period; the sentence itself is what must stay.
  if (text.includes(tail.replace(/[.]$/, ""))) return { text, restored: false };
  const body = text.trimEnd();
  return { text: body ? `${body}\n\n${tail}` : tail, restored: true };
}
